/**
 * For accessibility: The box is a <div> with a child <p>
 * The <div> holds the background image. Set it to role="img" and add alt-text
 * The <p> holds the text content.
 */

import { type ReactElement, memo, useContext, useRef } from "react";
import styled, { ThemeProvider } from "styled-components";
import { CurrentFilter, FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";

import { VisuallyHidden, strings } from "@quickpoint.me/shared";
import { useRecoilValue } from "recoil";
import type { BoxProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";
import { opacityHelper, resolveColorChoice } from "../../lib/cssHelpers";
import { mainCssRules } from "../../lib/cssTemplates";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { OwnerName } from "../../state/selectors";
import { HostId } from "../../state/ui";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import Blank from "./Blank";
import { StyledCaption } from "./Caption";

const StyledBox = styled.figure<BoxProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  --box-opacity: ${(props) => props.$boxOpacity};
  --box-blur: ${(props) => props.$boxBlur};
  display: flex;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  flex-wrap: wrap;
  text-wrap: balance;
  text-wrap: pretty;
  filter: blur(var(--box-blur)) opacity(var(--box-opacity));
   /* keep this here otherwise the text will become transparent */
  ${(props) => opacityHelper(props.$tapped, props.theme.opacity)}
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`}
  /* opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)}; */
  position:relative;
  width: fit-content;
  ${(props) => `${modifierStyled("box:", props)}`}
  background-color: transparent !important;
  /* this is required to prevent the backdrop from breaking out of the box */
  /*any modifiers will override it.*/
  backdrop-filter: opacity(0);
  background-size: cover;
  background-position: center;
  &::before {
    position: absolute;
    opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
    filter: blur(var(--box-blur));
    content: "";
    background: ${(props) =>
			resolveColorChoice(props.theme?.background, props.$filterColors)};
    /* the author's background: modifier wins — but "unset" must not
       clobber the theme default above */
    ${(props) =>
			props.$background && props.$background !== "unset"
				? `background: ${props.$background};`
				: ""}
    background-size: cover;
    background-position: center;
    ${(props) => `${modifierStyled("background:", props)}`}
    border: none; /* overwrite any border properties added by modifierStyled*/
    border-radius:  ${(props) =>
			`${props.theme.cornerRadius * props.$baseFontSize}px`};
    border-color: transparent;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width:100%;
    height:100%;
  }
  .content {
    text-align: start; 
    opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
    position: relative;
    padding-block-start: ${(props) =>
			`${props.theme.paddingBlockStart * props.$baseFontSizeRaw}px`};
    padding-block-end: ${(props) =>
			`${props.theme.paddingBlockEnd * props.$baseFontSizeRaw}px`};
    padding-inline-start: ${(props) =>
			`${props.theme.paddingInlineStart * props.$baseFontSizeRaw}px`};
    padding-inline-end: ${(props) =>
			`${props.theme.paddingInlineEnd * props.$baseFontSizeRaw}px`};
  }
`;

const Box = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const pageSize = useRecoilValue(PageSizeAtom);
	const baseFontSize = pageSize.baseFontSize;
	const tapped = elementData.tapped;
	const fonts = useRecoilValue(FilterFonts);
	const ownerName = useRecoilValue(OwnerName);
	const hostId = useRecoilValue(HostId);
	const boxBlur = elementData.modifier?.["blur:"]
		? elementData.modifier?.["blur:"]
		: "unset";
	const boxOpacity = elementData.modifier?.["opacity:"]
		? elementData.modifier?.["opacity:"]
		: "unset";
	const caption = elementData.modifier
		? elementData.modifier["caption:"]
		: undefined;
	const filter = useRecoilValue(CurrentFilter);

	const elRef = useRef(null);

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	return (
		<ThemeProvider theme={filter.box}>
			<StyledBox
				$background={elementData.background}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$boxBlur={boxBlur}
				$boxOpacity={boxOpacity}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement}  box`}
				data-atom-id={elementData.id}
				ref={elRef}
			>
				{/* role="img" on the container made every child presentational —
				    screen readers skipped the box's own text. The container is a
				    text box; a meaningful background image contributes its alt
				    text as hidden prose instead */}
				{elementData.altText &&
					String(elementData.background ?? "").startsWith("url(") && (
						<VisuallyHidden as="span">{elementData.altText}</VisuallyHidden>
					)}
				<blockquote aria-hidden={!elementData.tapped} className="content">
					{content}
				</blockquote>
				{caption && (
					<ThemeProvider theme={filter.caption}>
						<Blank atomId={atomId} pageIndex={pageIndex} type="blank:" />
						<StyledCaption
							$absolutePosition={false}
							$baseFontSize={baseFontSize}
							$baseFontSizeRaw={pageSize.baseFontSizeRaw}
							$elementIndex={atomId}
							$filterColors={filterColors}
							$fontMultiplier={fontMultiplier}
							$isInFlexBox={isInFlexBox}
							$pageSize={pageSize}
							$isInPortrait={!pageSize.landscape}
							$tapped={tapped}
							className="box-caption"
							$onePercentHeight={pageSize.onePercentHeight}
							$onePercentWidth={pageSize.onePercentWidth}
							$parentFontSizeMultiplier={filter.box.fontSize}
						>
							{caption}
						</StyledCaption>
					</ThemeProvider>
				)}
			</StyledBox>
		</ThemeProvider>
	);
};

export default memo(Box);
